import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function MyKennels({ playerId }) {
  const [kennels, setKennels] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [replies, setReplies] = useState({});

  useEffect(() => {
    if (!playerId || playerId === '') {
      setKennels([]);
      setLoading(false);
      return;
    }

    const fetchKennels = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/my-kennels/${playerId}`);
        if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
        const data = await res.json();
        setKennels(data.kennels || []);
      } catch (error) {
        setMessage('Failed to fetch kennels: ' + error.message);
        setKennels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchKennels();
  }, [playerId]);

  const sendReply = async (kennelId) => {
    try {
      const text = replies[kennelId] || '';
      if (!text) return;
      const res = await fetch(`${API_BASE_URL}/kennel-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kennelId, senderId: playerId, senderRole: 'player', text })
      });
      if (!res.ok) throw new Error(`Reply failed: ${res.status}`);
      const data = await res.json();
      setMessage(data.message);
      setKennels(kennels.map(k => 
        k._id === kennelId ? { ...k, messages: [...k.messages, { senderId: playerId, senderRole: 'player', text, timestamp: new Date(), unread: true }] } : k
      ));
      setReplies(prev => ({ ...prev, [kennelId]: '' }));
    } catch (error) {
      setMessage('Reply failed: ' + error.message);
    }
  };

  const markMessageRead = async (kennelId, msgIdx) => {
    try {
      const kennel = kennels.find(k => k._id === kennelId);
      const updatedKennel = { ...kennel, messages: kennel.messages.map((m, i) => i === msgIdx ? { ...m, unread: false } : m) };
      const res = await fetch(`${API_BASE_URL}/kennel/${kennel._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedKennel)
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
      setKennels(kennels.map(k => k._id === kennelId ? updatedKennel : k));
    } catch (error) {
      setMessage('Failed to mark as read: ' + error.message);
    }
  };

  const clearDeniedKennel = async (kennelId) => {
    try {
      const kennel = kennels.find(k => k._id === kennelId);
      if (kennel.status !== 'Denied') return;
      const res = await fetch(`${API_BASE_URL}/kennel/${kennelId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error(`Clear failed: ${res.status}`);
      const data = await res.json();
      setKennels(kennels.filter(k => k._id !== kennelId));
      setMessage(data.message);
    } catch (error) {
      setMessage('Clear failed: ' + error.message);
    }
  };

  if (!playerId || playerId === '') return <p style={{ textAlign: 'center', color: '#888' }}>Please log in to view your kennels.</p>;
  if (loading) return <p style={{ textAlign: 'center', color: '#888' }}>Loading kennels...</p>;

  const activeKennels = kennels.filter(k => !k.retired);
  const retiredKennels = kennels.filter(k => k.retired);

  const kennelContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)', // 3 columns
    gap: '20px', // Space between kennels
  };

  const kennelCardStyle = {
    padding: '15px',
    backgroundColor: '#fff',
    borderRadius: '5px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    transition: 'transform 0.2s',
    cursor: 'pointer', // Indicates clickability
    textDecoration: 'none', // Removes underline from Link
    color: 'inherit', // Inherits text color
  };

  const kennelCardHoverStyle = {
    transform: 'scale(1.02)',
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <h1 style={{ color: '#1E88E5', marginBottom: '20px', textAlign: 'center' }}>My Kennels</h1>
      {message && (
        <div style={{
          backgroundColor: '#BBDEFB',
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '5px',
          color: '#333',
          fontWeight: 'bold',
          textAlign: 'center'
        }}>
          {message}
        </div>
      )}

      {/* Active Kennels */}
      <h2 style={{ color: '#4CAF50', marginBottom: '15px' }}>Active Kennels</h2>
      {activeKennels.length > 0 ? (
        <div style={{
          backgroundColor: '#f9f9f9',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
          border: '1px solid #BBDEFB',
          marginBottom: '30px'
        }}>
          <div style={kennelContainerStyle}>
            {activeKennels.map((kennel) => (
              <Link
                key={kennel._id}
                to={`/kennel/${kennel._id}`}
                style={kennelCardStyle}
                onMouseOver={(e) => e.currentTarget.style.transform = kennelCardHoverStyle.transform}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <strong style={{ color: '#1E88E5', fontSize: '1.2em' }}>{kennel.kennelName}</strong>
                <br />
                {kennel.status !== 'Approved' && (
                  <p style={{ color: kennel.status === 'Denied' ? '#EF5350' : '#1976D2' }}>Status: {kennel.status}</p>
                )}
                {kennel.status === 'Approved' && kennel.artUrl && (
                  <img src={kennel.artUrl} alt={kennel.kennelName} style={{ maxWidth: '150px', borderRadius: '8px', marginTop: '10px' }} />
                )}
                {kennel.denyReason && <p style={{ color: '#EF5350' }}>Denial Reason: {kennel.denyReason}</p>}
                {(kennel.status === 'Pending' || kennel.status === 'Denied') && (
                  <>
                    <h3 style={{ color: '#1E88E5', margin: '10px 0' }}>Messages</h3>
                    {kennel.messages?.length > 0 ? (
                      <ul style={{ paddingLeft: '20px', listStyle: 'none' }}>
                        {kennel.messages.map((msg, idx) => (
                          <li
                            key={idx}
                            style={{ color: msg.unread && msg.senderRole === 'admin' ? '#EF5350' : '#333', marginBottom: '5px' }}
                          >
                            <strong>{msg.senderRole}:</strong> {msg.text} <br />
                            <small style={{ color: '#666' }}>({new Date(msg.timestamp).toLocaleString()})</small>
                            {msg.unread && msg.senderRole === 'admin' && (
                              <button
                                onClick={(e) => { e.preventDefault(); markMessageRead(kennel._id, idx); }}
                                style={{
                                  marginLeft: '10px',
                                  padding: '4px 10px',
                                  backgroundColor: '#1E88E5',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.3s'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#1E88E5'}
                              >
                                Mark Read
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p style={{ color: '#888' }}>No messages.</p>
                    )}
                    <textarea
                      value={replies[kennel._id] || ''}
                      onChange={(e) => setReplies({ ...replies, [kennel._id]: e.target.value })}
                      onClick={(e) => e.stopPropagation()} // Prevents Link navigation on click
                      placeholder="Reply to admin..."
                      style={{ width: '100%', maxWidth: '300px', marginTop: '10px', padding: '5px', borderRadius: '5px', border: '1px solid #ccc' }}
                    />
                    <div style={{ marginTop: '10px' }}>
                      <button
                        onClick={(e) => { e.preventDefault(); sendReply(kennel._id); }}
                        style={{
                          padding: '8px 15px',
                          backgroundColor: '#1E88E5',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          marginRight: '10px',
                          transition: 'background-color 0.3s'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#1E88E5'}
                      >
                        Send Reply
                      </button>
                      {kennel.status === 'Denied' && (
                        <button
                          onClick={(e) => { e.preventDefault(); clearDeniedKennel(kennel._id); }}
                          style={{
                            padding: '8px 15px',
                            backgroundColor: '#EF5350',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            transition: 'background-color 0.3s'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#F44336'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#EF5350'}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </>
                )}
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <p style={{ textAlign: 'center', color: '#888', fontSize: '16px' }}>No active kennels found.</p>
      )}

      {/* Retired Kennels */}
      <h2 style={{ color: '#4CAF50', marginBottom: '15px' }}>Retired Kennels</h2>
      {retiredKennels.length > 0 ? (
        <div style={{
          backgroundColor: '#f9f9f9',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
          border: '1px solid #BBDEFB'
        }}>
          <div style={kennelContainerStyle}>
            {retiredKennels.map((kennel) => (
              <Link
                key={kennel._id}
                to={`/kennel/${kennel._id}`}
                style={kennelCardStyle}
                onMouseOver={(e) => e.currentTarget.style.transform = kennelCardHoverStyle.transform}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <strong style={{ color: '#1E88E5', fontSize: '1.2em' }}>{kennel.kennelName}</strong>
                <br />
                <p style={{ color: '#555' }}>Retired</p>
                {kennel.status === 'Approved' && kennel.artUrl && (
                  <img src={kennel.artUrl} alt={kennel.kennelName} style={{ maxWidth: '150px', borderRadius: '8px', marginTop: '10px' }} />
                )}
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <p style={{ textAlign: 'center', color: '#888', fontSize: '16px' }}>No retired kennels found.</p>
      )}
    </div>
  );
}

export default MyKennels;